import { useEffect } from 'react';

// iOS/iPadOS 27 WebKit paints a MediaStream <video> with `object-fit: cover`
// incorrectly (it reports support, so CSS.supports can't detect it): the
// preview shrinks to a letterboxed strip inside its box. @incodetech/web 2.3.2
// works around it in useCoverMedia by sizing the video explicitly to cover
// geometry on exactly those builds; this is the same workaround. Every other
// browser keeps native object-fit, with no listeners or inline styles.
export function needsExplicitCover(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    if (/(?:iPhone|iPad|iPod)/.test(ua) && ua.match(/CPU (?:iPhone )?OS (\d+)[_.]/)?.[1] === '27') return true;
    const desktopModeIPad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return desktopModeIPad && ua.match(/Version\/(\d+)[._]/)?.[1] === '27';
}

export function applyExplicitCover(video: HTMLVideoElement): void {
    const box = video.parentElement?.getBoundingClientRect();
    const { videoWidth: w, videoHeight: h } = video;
    if (!box || !w || !h || !box.width || !box.height) return;
    const scale = Math.max(box.width / w, box.height / h);
    video.style.position = 'absolute';
    video.style.width = `${(w * scale).toFixed(2)}px`;
    video.style.height = `${(h * scale).toFixed(2)}px`;
    video.style.left = `${((box.width - w * scale) / 2).toFixed(2)}px`;
    video.style.top = `${((box.height - h * scale) / 2).toFixed(2)}px`;
}

// The video's parent must be a positioned box with a definite size.
export function useExplicitCover(
    videoRef: React.RefObject<HTMLVideoElement | null>,
    stream: MediaStream | undefined,
    enabled: boolean,
): void {
    useEffect(() => {
        const video = videoRef.current;
        if (!enabled || !video || !needsExplicitCover()) return undefined;

        const apply = () => applyExplicitCover(video);
        video.addEventListener('loadedmetadata', apply);
        video.addEventListener('resize', apply);
        window.addEventListener('resize', apply);
        window.addEventListener('orientationchange', apply);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(apply);
        if (video.parentElement) observer?.observe(video.parentElement);
        apply();

        return () => {
            video.removeEventListener('loadedmetadata', apply);
            video.removeEventListener('resize', apply);
            window.removeEventListener('resize', apply);
            window.removeEventListener('orientationchange', apply);
            observer?.disconnect();
        };
    }, [videoRef, stream, enabled]);
}
